import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import useIntegration from '../../hooks/useIntegration';
import useNotification from '../../hooks/useNotification';

const integrationTypes = [
  { value: 'JIRA', label: 'Atlassian JIRA' },
  { value: 'ASANA', label: 'Asana' },
  { value: 'TRELLO', label: 'Trello' }
];

const IntegrationForm = ({ projectId, onSuccess }) => {
  const { connectToExternalSystem, loading, error } = useIntegration();
  const { showNotification } = useNotification();
  
  const [integrationType, setIntegrationType] = useState('');
  const [configFields, setConfigFields] = useState({});
  
  // Get config fields based on integration type
  const getConfigFields = (type) => {
    switch (type) {
      case 'JIRA':
        return {
          baseUrl: '',
          email: '',
          apiToken: '',
          projectKey: ''
        };
      case 'ASANA':
        return {
          accessToken: '',
          workspaceId: '',
          projectId: ''
        };
      case 'TRELLO':
        return {
          apiKey: '',
          token: '',
          boardId: ''
        };
      default:
        return {};
    }
  };
  
  // Handle integration type change
  const handleTypeChange = (e) => {
    const type = e.target.value;
    setIntegrationType(type);
    setConfigFields(getConfigFields(type));
  };
  
  // Handle config field change
  const handleConfigChange = (field) => (e) => {
    setConfigFields({
      ...configFields,
      [field]: e.target.value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const integration = await connectToExternalSystem(
        projectId,
        integrationType,
        configFields
      );
      
      showNotification({
        message: `Successfully connected to ${integrationType}`,
        type: 'success'
      });
      
      if (onSuccess) {
        onSuccess(integration);
      }
      
      // Reset form
      setIntegrationType('');
      setConfigFields({});
    } catch (err) {
      console.error('Integration error:', err);
      // Error is already handled by the hook
    }
  };
  
  // Render config fields based on integration type
  const renderConfigFields = () => {
    if (!integrationType) return null;
    
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Connection Configuration
        </Typography>
        
        {Object.keys(configFields).map(field => (
          <TextField
            key={field}
            className="single-line-field"
            margin="normal"
            label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
            value={configFields[field]}
            onChange={handleConfigChange(field)}
            disabled={loading}
            type={field.includes('token') || field.includes('key') || field.includes('password') ? 'password' : 'text'}
          />
        ))}
      </Box>
    );
  };
  
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Connect to External System
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Connect your project to an external project management system to sync features and user stories.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="integration-type-label">Integration Type</InputLabel>
          <Select
            labelId="integration-type-label"
            id="integration-type"
            value={integrationType}
            label="Integration Type"
            onChange={handleTypeChange}
            disabled={loading}
            className="single-line-field"
          >
            {integrationTypes.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {renderConfigFields()}
        
        {integrationType && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 3 }} />
            
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !integrationType}
              sx={{ mr: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Connect'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setIntegrationType('');
                setConfigFields({});
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default IntegrationForm;